import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import { 
  Save, 
  Print, 
  Description,
  Rotate90DegreesCcw,
  Image,
  Business,
  ContentCopy
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';

const translations = {
  ar: {
    title: 'إعدادات الطباعة',
    subtitle: 'تخصيص تنسيق الطباعة وإعدادات المستندات',
    pageSettings: 'إعدادات الصفحة',
    contentSettings: 'إعدادات المحتوى',
    advanced: 'متقدم',
    paperSize: 'حجم الورق',
    orientation: 'الاتجاه',
    portrait: 'عمودي',
    landscape: 'أفقي',
    headerText: 'نص الرأس',
    footerText: 'نص التذييل',
    showLogo: 'إظهار الشعار',
    showCompanyInfo: 'إظهار بيانات الشركة',
    autoprint: 'طباعة تلقائية',
    copies: 'عدد النسخ',
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل حفظ الإعدادات'
  },
  en: {
    title: 'Print Settings',
    subtitle: 'Customize print format and document settings',
    pageSettings: 'Page Settings',
    contentSettings: 'Content Settings',
    advanced: 'Advanced',
    paperSize: 'Paper Size',
    orientation: 'Orientation',
    portrait: 'Portrait',
    landscape: 'Landscape',
    headerText: 'Header Text',
    footerText: 'Footer Text',
    showLogo: 'Show Logo',
    showCompanyInfo: 'Show Company Info',
    autoprint: 'Auto Print',
    copies: 'Number of Copies',
    saveChanges: 'Save Changes',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings'
  }
};

export default function PrintSettingsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [formData, setFormData] = useState({
    paperSize: 'A4',
    orientation: 'portrait',
    headerText: '',
    footerText: '',
    showLogo: true,
    showCompanyInfo: true,
    autoprint: false,
    copies: 1
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'copies' ? parseInt(value) || 1 : value)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_BASE_URL}/SystemSettings/bulk`, 
        Object.entries(formData).map(([key, value]) => ({
          settingKey: key,
          settingValue: value.toString(),
          category: 'Print'
        })),
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setMessage({ type: 'success', text: t.saveSuccess });
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #868f96 0%, #596164 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(134, 143, 150, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Print sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Page Settings Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(134, 143, 150, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Description sx={{ color: '#868f96', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600">
                  {t.pageSettings}
                </Typography>
              </Box>
              <Chip 
                label={`${formData.paperSize} - ${formData.orientation === 'portrait' ? t.portrait : t.landscape}`}
                size="small"
                sx={{ bgcolor: 'rgba(134, 143, 150, 0.1)', fontWeight: 600 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                select
                label={t.paperSize}
                name="paperSize"
                value={formData.paperSize}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="A4">A4 (210 × 297 mm)</MenuItem>
                <MenuItem value="A5">A5 (148 × 210 mm)</MenuItem>
                <MenuItem value="Letter">Letter (8.5 × 11 in)</MenuItem>
                <MenuItem value="Legal">Legal (8.5 × 14 in)</MenuItem>
              </TextField>

              <TextField
                fullWidth
                select
                label={t.orientation}
                name="orientation"
                value={formData.orientation}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="portrait">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Description sx={{ fontSize: 18 }} />
                    <span>{t.portrait}</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="landscape">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Rotate90DegreesCcw sx={{ fontSize: 18 }} />
                    <span>{t.landscape}</span>
                  </Stack>
                </MenuItem>
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        {/* Content Settings Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(89, 97, 100, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Business sx={{ color: '#596164', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.contentSettings}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t.headerText}
                name="headerText"
                value={formData.headerText}
                onChange={handleChange}
                multiline
                rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                fullWidth
                label={t.footerText}
                name="footerText"
                value={formData.footerText}
                onChange={handleChange}
                multiline
                rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <Divider />

              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showLogo}
                      onChange={handleChange}
                      name="showLogo"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Image sx={{ fontSize: 20, color: formData.showLogo ? '#868f96' : '#bbb' }} />
                      <span>{t.showLogo}</span>
                    </Stack>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showCompanyInfo}
                      onChange={handleChange}
                      name="showCompanyInfo"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Business sx={{ fontSize: 20, color: formData.showCompanyInfo ? '#868f96' : '#bbb' }} />
                      <span>{t.showCompanyInfo}</span>
                    </Stack>
                  }
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Advanced Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(134, 143, 150, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <ContentCopy sx={{ color: '#868f96', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.advanced}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t.copies}
                name="copies"
                type="number"
                value={formData.copies}
                onChange={handleChange}
                inputProps={{ min: 1, max: 10 }}
                sx={{ 
                  maxWidth: 250,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 } 
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoprint}
                    onChange={handleChange}
                    name="autoprint"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Print sx={{ fontSize: 20, color: formData.autoprint ? '#868f96' : '#bbb' }} />
                    <span>{t.autoprint}</span>
                  </Stack>
                }
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #868f96 0%, #596164 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(134, 143, 150, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #747d84 0%, #4a5154 100%)',
              boxShadow: '0 6px 20px rgba(134, 143, 150, 0.6)',
            }
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}